/**
 * SupportPage — /support dedicated console surface (SCK-549 PR-8).
 *
 * Two-column layout:
 *   Left  — full-page SupportWidget variant, more real estate than
 *           the floating popover so a long back-and-forth fits.
 *   Right — YourTicketsPanel: caller's tickets with SLA at a glance.
 *
 * The same SupportWidget component drives the floating widget (PR-7);
 * we import it in "full" variant instead of duplicating the chat UI.
 *
 * Renders inside the MainLayout — the persistent NAV_ITEMS drawer +
 * app bar are still visible around us. On narrow viewports the columns
 * stack; the chat sits on top so a mobile user can still send messages
 * without scrolling past a long ticket list.
 */

import { Box, Grid, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";

import {
  heroText,
  motionVariants,
  pageBackground,
} from "../../helpers/designSystem";
import { SupportWidget } from "../../features/support";
import YourTicketsPanel from "./YourTicketsPanel";

export default function SupportPage() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        ...pageBackground(isDarkMode),
        mx: -3,
        mt: -3,
        px: 3,
        pt: 3,
        pb: 4,
        minHeight: "calc(100vh - 96px)",
      }}
      data-testid="support-page"
    >
      <Box
        component={motion.div}
        initial="initial"
        animate="animate"
        variants={motionVariants?.heroEnter}
        sx={{ mb: 3 }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            ...heroText(isDarkMode),
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}
        >
          Support
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.5, maxWidth: 640 }}
        >
          Ask the bot anything about your apps, builds, or docs.
          When it can't resolve on its own, it files a ticket and
          hands it off to a human — you'll find every ticket
          you've opened on the right.
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={7} lg={7}>
          <Box
            sx={{
              height: { xs: 520, md: "calc(100vh - 220px)" },
              minHeight: 480,
            }}
          >
            <SupportWidget variant="full" />
          </Box>
        </Grid>
        <Grid item xs={12} md={5} lg={5}>
          <YourTicketsPanel />
        </Grid>
      </Grid>
    </Box>
  );
}
